import React, { type JSX } from 'react';

import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { useAppSelector } from 'hooks/redux';

import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import GuideButton from 'components/GuideButton';
import SimplifiAndQBPLogo from 'components/SimplifiAndQBPLogo';
import { makeStyles } from 'tss-react/mui';
import TopBarNavItems from './TopBarNavItems';

const useStyles = makeStyles<{ hideNavButtons: boolean }>()((theme, { hideNavButtons }) => ({
  appBar: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    zIndex: hideNavButtons ? theme.zIndex.modal : theme.zIndex.appBar,
  },
  toolbar: {
    justifyContent: 'space-between',
  },
  titleBlock: {
    minWidth: 0,
  },
  tabsRow: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: theme.spacing(3),
  },
  tabsContainer: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
}));

export type TopBarProps = {
  /** Main heading text. Ignored when logo is set. */
  title?: string;
  /** DOM id for the title element (e.g. for Cypress). */
  titleId?: string;
  /** When true, shows the SimplifiAndQBPLogo . When a node, shows that instead of the title. */
  logo?: React.ReactNode | boolean;
  /** When set, shows the useGuideButton next to the title or logo. */
  guideId?: string;
  /** Content shown on the right side of the toolbar row. */
  actionArea?: React.ReactNode;
  /** Content shown on the right side of the tabs row. Only rendered when `tabs` is set. */
  tabsActionArea?: React.ReactNode;
  /** Optional content in a second row below the toolbar (e.g. tab navigation). */
  tabs?: React.ReactNode;
  /** DOM id for the bar; default 'page-header'. */
  id?: string;
};

const TopBar = ({
  title,
  titleId,
  logo,
  guideId,
  actionArea,
  tabsActionArea,
  tabs,
  id = 'page-header',
}: TopBarProps): JSX.Element => {
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const { classes } = useStyles({ hideNavButtons });
  const theme = useTheme();
  const isSmallWindow = useMediaQuery(theme.breakpoints.down('md'));

  const titleOrLogo = (() => {
    if (logo === true) return <SimplifiAndQBPLogo showLogo />;
    if (logo != null && logo !== false) return logo as React.ReactNode;
    if (hideNavButtons) return <SimplifiAndQBPLogo />;
    if (title != null) {
      return (
        <Stack
          direction='row'
          gap={1}
          id={titleId ?? 'page-main-title'}
          alignItems='center'
          className={classes.titleBlock}
        >
          <Typography
            variant={isSmallWindow ? 'titleMedium' : 'headlineMedium'}
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {title}
          </Typography>
        </Stack>
      );
    }
    return null;
  })();

  const hasTabs = tabs != null;

  return (
    <AppBar
      id={id}
      className={classes.appBar}
      position='sticky'
      color='inherit'
      elevation={12}
      sharedcomponentid='TopBar'
    >
      <Toolbar className={classes.toolbar} disableGutters={false}>
        <Stack direction='row' gap={1} alignItems='center'>
          {titleOrLogo}
          {guideId != null ? <GuideButton guideId={guideId} /> : null}
        </Stack>
        {actionArea ?? null}
        <TopBarNavItems />
      </Toolbar>
      {hasTabs ? <Divider /> : null}
      {hasTabs ? (
        <Stack className={classes.tabsRow}>
          <div className={classes.tabsContainer}>{tabs}</div>
          {tabsActionArea ?? null}
        </Stack>
      ) : null}
    </AppBar>
  );
};

export default TopBar;
